import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import Layout from "./components/layout/Layout";
import Dashboard from "./components/dashboard";
import Clients from "./components/clients";
import Offers from "./components/offers";
import PaymentsV2 from "./components/paymentsV2";
import Team from "./components/team";
import Tools from "./components/tools";
import Departments from "./components/departments";
import Dividends from "./components/dividends";
import ErrorBoundary from "./components/ui/ErrorBoundary";
import Transactions from "./components/expenses";
import Settings from "./components/settings";
import LoginScreen from "./components/auth/LoginScreen";
import { useDashboard } from "./hooks/useDashboard";
import { useAuth } from "./hooks/useAuth";
import { useGhlSync } from "./hooks/useGhlSync";
import { useStripePayments } from "./hooks/useStripePayments";
import { findClientByEmail, normEmail } from "./lib/emailUtils";
import { mapGhlInvoiceToPayment, mergeGhlWithManual, applyGhlOfferOverrides } from "./lib/ghlSync";
import { mapStripeChargeToPayment, mergeStripeWithOthers } from "./lib/stripeSync";

const TABS = {
  dashboard: Dashboard,
  departments: Departments,
  clients: Clients,
  offers: Offers,
  paymentsV2: PaymentsV2,
  team: Team,
  tools: Tools,
  transactions: Transactions,
  dividends: Dividends,
  settings: Settings,
};

// Read the current tab from URL hash, falling back to "dashboard".
const readTabFromHash = () => {
  const hash = (typeof window !== "undefined" ? window.location.hash : "").replace(/^#\/?/, "");
  return hash && TABS[hash] ? hash : "dashboard";
};

export default function App() {
  const auth = useAuth();
  const dashboard = useDashboard(auth.user);
  const ghl = useGhlSync();
  const stripe = useStripePayments();
  const [activeTab, setActiveTab] = useState(readTabFromHash);
  const [pendingClientId, setPendingClientId] = useState(null);

  // Global date filter — visible on every page (top of Layout), consumed by
  // any tab that has date-bound data (Payments, Dashboard, Tools, etc.).
  // Persisted to localStorage so it survives reloads.
  const [dateFilter, setDateFilter] = useState(() => {
    if (typeof window === "undefined") return { year: new Date().getFullYear(), fromMonth: null, toMonth: null };
    try {
      const raw = window.localStorage.getItem("uc.dateFilter");
      if (raw) return JSON.parse(raw);
    } catch {/* fall through */}
    return { year: new Date().getFullYear(), fromMonth: null, toMonth: null };
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem("uc.dateFilter", JSON.stringify(dateFilter));
  }, [dateFilter]);

  // Reflect the active tab in the URL hash so refresh / bookmarks / back+forward
  // all land on the same place.
  useEffect(() => {
    const wanted = `#${activeTab}`;
    if (window.location.hash !== wanted) {
      window.history.replaceState(null, "", wanted);
    }
  }, [activeTab]);

  // Listen for browser back/forward navigation.
  useEffect(() => {
    const onHashChange = () => {
      const next = readTabFromHash();
      setActiveTab((prev) => (prev !== next ? next : prev));
    };
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  const openClient = useCallback((id) => {
    if (!id) return;
    setPendingClientId(id);
    setActiveTab("clients");
  }, []);

  const clearPendingClient = useCallback(() => setPendingClientId(null), []);

  // Jump to Departments tab pre-selected on a specific department.
  // We pass it via localStorage so the Departments tab can pick it up on mount.
  // Cleaner than threading another piece of state through props.
  const openDepartment = useCallback((deptName) => {
    if (!deptName) return;
    try {
      window.localStorage.setItem("uc.pendingDept", deptName);
    } catch { /* ignore */ }
    setActiveTab("departments");
  }, []);

  // Compute the merged payment universe ONCE here so every tab — Offers,
  // Dashboard per-offer view, Payments, etc. — sees the same data.
  // Previously only the Clients tab merged GHL in locally, so analytics
  // elsewhere undercounted revenue. Returns state with payments replaced
  // by the merged list. All other state fields pass through unchanged.
  const mergedState = useMemo(() => {
    const base = dashboard.state;
    const ghlOfferMap = base.settings?.ghlInvoiceOfferMap || {};
    // Phase C (2026-05-17): GHL only contributes UNPAID invoices to the
    // merged universe. Paid GHL records would duplicate Stripe charges that
    // collected the same invoice (often weeks later than the issue date,
    // beyond any reasonable dedup window). Stripe owns "paid"; GHL owns AR.
    const ghlMapped = (ghl.invoices || [])
      .map((inv) => mapGhlInvoiceToPayment(inv, base.clients, base.offers))
      .filter((p) => p && p.status !== "paid");
    const ghlPayments = applyGhlOfferOverrides(ghlMapped, ghlOfferMap);
    const manualPlusGhl = mergeGhlWithManual(base.payments, ghlPayments);
    const stripePayments = (stripe.charges || [])
      .map((c) => mapStripeChargeToPayment(c, base.clients))
      .filter(Boolean);
    // mergeStripeWithOthers still dedups Stripe against any manual paid
    // entries (e.g. cash payments Ahmed typed in before Stripe sync was
    // enabled). GHL-paid is already filtered out above so no overlap there.
    const merged = mergeStripeWithOthers(manualPlusGhl, stripePayments);
    return { ...base, payments: merged };
  }, [dashboard.state, ghl.invoices, stripe.charges]);

  // Auto-create clients for unmatched GHL customers. Runs once data arrives
  // and re-runs as state.clients changes; in-memory ref dedupes within a session.
  const seenEmailsRef = useRef(new Set());
  useEffect(() => {
    if (dashboard.loading) return;
    if (!ghl.invoices || ghl.invoices.length === 0) return;
    const clients = dashboard.state.clients;
    const toCreate = [];
    for (const inv of ghl.invoices) {
      const email = normEmail(inv.customerEmail || "");
      if (!email) continue;
      if (seenEmailsRef.current.has(email)) continue;
      if (findClientByEmail(email, clients)) {
        seenEmailsRef.current.add(email);
        continue;
      }
      seenEmailsRef.current.add(email);
      toCreate.push({
        name: inv.customerName || inv.customerCompany || email,
        email,
        country: "",
        type: "Other",
        primaryType: "oneoff",
        primaryOfferId: "",
        startDate: inv.issueDate ? String(inv.issueDate).slice(0, 10) : "",
        acquisitionSource: "Other",
        funnel: "",
        notes: "Auto-created from GHL invoice",
      });
    }
    if (toCreate.length > 0) {
      toCreate.forEach((c) => dashboard.addClient(c));
    }
  }, [ghl.invoices, dashboard.state.clients, dashboard.loading, dashboard.addClient, dashboard]);

  // Auto-create clients for unmatched Stripe-paid customers. Mirrors the GHL
  // flow but uses Stripe charges (succeeded / partially_refunded only).
  // Lifted from PaymentsV2 to App-level so new clients appear before Ahmed
  // ever opens the Payments V2 tab. The new client's startDate is the
  // EARLIEST charge date for that email — that's the date they paid their
  // first invoice, which is what Ahmed considers their start.
  const seenStripeEmailsRef = useRef(new Set());
  useEffect(() => {
    if (dashboard.loading) return;
    if (!stripe.charges || stripe.charges.length === 0) return;
    const clients = dashboard.state.clients;

    // First pass: for each email, find the EARLIEST eligible charge (and
    // its customer name from that earliest charge too, for consistency).
    const earliestByEmail = new Map();
    for (const c of stripe.charges) {
      if (c.status !== "succeeded" && c.status !== "partially_refunded") continue;
      const email = normEmail(c.customerEmail || "");
      if (!email || !c.date) continue;
      const existing = earliestByEmail.get(email);
      if (!existing || c.date < existing.date) earliestByEmail.set(email, c);
    }

    // Second pass: create one client per new email using its earliest charge.
    const toCreate = [];
    for (const [email, earliest] of earliestByEmail) {
      if (seenStripeEmailsRef.current.has(email)) continue;
      if (findClientByEmail(email, clients)) {
        seenStripeEmailsRef.current.add(email);
        continue;
      }
      seenStripeEmailsRef.current.add(email);
      toCreate.push({
        name: earliest.customerName || email,
        email,
        country: "",
        type: "Other",
        primaryType: "oneoff",
        primaryOfferId: "",
        startDate: earliest.date || "",
        acquisitionSource: "Other",
        funnel: "",
        notes: "Auto-created from Stripe charge",
      });
    }
    if (toCreate.length > 0) {
      toCreate.forEach((c) => dashboard.addClient(c));
    }
  }, [stripe.charges, dashboard.state.clients, dashboard.loading, dashboard.addClient, dashboard]);

  // One-time backfill on app load: walk every existing client and push their
  // startDate back to their earliest Stripe charge date if the current value
  // is later (or empty). Fixes the historical bug where auto-create used
  // whichever charge happened to be iterated first — usually the most recent
  // one because charges come back newest-first from Supabase.
  // Auto-refresh from Stripe on app load so new charges show up without
  // Ahmed having to click the Refresh button. Runs once per session, only
  // if the cache is "stale" (last sync was more than 5 minutes ago).
  // The check prevents wasteful API calls when the user reloads the page
  // multiple times in a few minutes.
  const stripeAutoRefreshedRef = useRef(false);
  useEffect(() => {
    if (stripeAutoRefreshedRef.current) return;
    if (!auth.user) return;
    if (stripe.loading || stripe.refreshing) return;
    const lastSync = stripe.lastSyncedAt ? new Date(stripe.lastSyncedAt).getTime() : 0;
    const ageMinutes = (Date.now() - lastSync) / 60000;
    if (lastSync && ageMinutes < 5) {
      // Recently synced — skip this load, mark as done so we don't loop.
      stripeAutoRefreshedRef.current = true;
      return;
    }
    stripeAutoRefreshedRef.current = true;
    stripe.refresh();
  }, [auth.user, stripe.loading, stripe.refreshing, stripe.lastSyncedAt, stripe.refresh]);

  const startDateBackfilledRef = useRef(false);
  useEffect(() => {
    if (startDateBackfilledRef.current) return;
    if (dashboard.loading) return;
    if (!stripe.charges || stripe.charges.length === 0) return;
    const clients = dashboard.state.clients;
    if (!clients || clients.length === 0) return;

    // Resolve every charge to a clientId (override wins, else email match)
    // and track the earliest date per resolved client.
    const earliestByClientId = new Map();
    const clientById = new Map(clients.map((c) => [c.id, c]));
    for (const c of stripe.charges) {
      if (c.status !== "succeeded" && c.status !== "partially_refunded") continue;
      if (!c.date) continue;
      let clientId = null;
      if (c.clientIdOverride && clientById.has(c.clientIdOverride)) {
        clientId = c.clientIdOverride;
      }
      if (!clientId) {
        const matched = findClientByEmail(c.customerEmail, clients);
        if (matched) clientId = matched.id;
      }
      if (!clientId) continue;
      const existing = earliestByClientId.get(clientId);
      if (!existing || c.date < existing) earliestByClientId.set(clientId, c.date);
    }

    // For each client, if their startDate is later than their earliest
    // resolved charge (or empty), push it back to the earliest charge.
    let fixed = 0;
    for (const client of clients) {
      const earliest = earliestByClientId.get(client.id);
      if (!earliest) continue;
      if (!client.startDate || client.startDate > earliest) {
        dashboard.updateClient(client.id, { startDate: earliest });
        fixed += 1;
      }
    }
    if (fixed > 0) {
      console.log(`[startDate backfill] corrected ${fixed} clients to their earliest Stripe charge date`);
    }
    startDateBackfilledRef.current = true;
  }, [stripe.charges, dashboard.state.clients, dashboard.loading, dashboard.updateClient]);

  if (auth.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-pale-gray dark:bg-[var(--bg-page)]">
        <div className="text-sm text-brand-slate">Loading…</div>
      </div>
    );
  }

  if (!auth.user) {
    return <LoginScreen onSignIn={auth.signIn} />;
  }

  const Tab = TABS[activeTab] || Dashboard;

  return (
    <Layout
      activeTab={activeTab}
      onTabChange={setActiveTab}
      darkMode={dashboard.state.settings.darkMode}
      onToggleDark={() => dashboard.updateSettings({ darkMode: !dashboard.state.settings.darkMode })}
      user={auth.user}
      onSignOut={auth.signOut}
      dateFilter={dateFilter}
      onDateFilterChange={setDateFilter}
    >
      {dashboard.loading ? (
        <div className="text-sm text-brand-slate py-20 text-center">Loading your data…</div>
      ) : (
        <ErrorBoundary key={activeTab}>
          <Tab
            state={mergedState}
            dashboard={dashboard}
            ghl={ghl}
            stripe={stripe}
            dateFilter={dateFilter}
            onOpenClient={openClient}
            onJumpToDept={openDepartment}
            initialClientId={activeTab === "clients" ? pendingClientId : null}
            onClearInitialClient={clearPendingClient}
          />
        </ErrorBoundary>
      )}
    </Layout>
  );
}
